// src/components/admin/analytics/RevenueTab.jsx
import { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  FaDollarSign,
  FaFileInvoice,
  FaCheckCircle,
  FaChartLine,
} from 'react-icons/fa';
import { FaArrowTrendUp, FaArrowTrendDown } from 'react-icons/fa6';
import useRevenueAnalytics from '../../../hooks/useRevenueAnalytics';

const fmt = (val) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(val);

const fmtDate = (date) =>
  date
    ? new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(date)
    : '—';

const STATUS_CONFIG = {
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700' },
  open: { label: 'Awaiting', color: 'bg-yellow-100 text-yellow-700' },
  payment_failed: { label: 'Failed', color: 'bg-red-100 text-red-700' },
};

const getStatus = (invoice) => {
  if (!invoice.stripeInvoiceStatus || invoice.stripeInvoiceStatus === 'open') return 'open';
  return invoice.stripeInvoiceStatus;
};

const StatCard = ({ title, value, sub, icon: Icon, color, bgColor, change }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center justify-between mb-4">
      <div className={`${bgColor} p-3 rounded-full`}>
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
      {change !== undefined && (
        <div
          className={`flex items-center text-sm font-medium ${
            change >= 0 ? 'text-green-600' : 'text-red-600'
          }`}
        >
          {change >= 0 ? (
            <FaArrowTrendUp className="w-4 h-4 mr-1" />
          ) : (
            <FaArrowTrendDown className="w-4 h-4 mr-1" />
          )}
          {Math.abs(change)}%
        </div>
      )}
    </div>
    <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

const FILTERS = ['all', 'paid', 'open', 'payment_failed'];
const FILTER_LABELS = { all: 'All', paid: 'Paid', open: 'Awaiting', payment_failed: 'Failed' };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {p.dataKey === 'revenue' ? fmt(p.value) : p.value}
        </p>
      ))}
    </div>
  );
};

const RevenueTab = () => {
  const { invoices, stats, loading } = useRevenueAnalytics();
  const [filter, setFilter] = useState('all');

  const filtered =
    filter === 'all'
      ? invoices
      : invoices.filter((i) => getStatus(i) === filter);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
              <div className="w-12 h-12 bg-gray-200 rounded-full mb-4" />
              <div className="h-3 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-7 bg-gray-200 rounded w-1/2" />
            </div>
          ))}
        </div>
        <div className="bg-white rounded-lg shadow p-6 animate-pulse h-80" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Collected"
          value={fmt(stats.totalRevenue)}
          sub={`${stats.paidCount} paid invoices`}
          icon={FaDollarSign}
          color="text-green-600"
          bgColor="bg-green-100"
        />
        <StatCard
          title="This Month"
          value={fmt(stats.monthlyRevenue)}
          sub={`vs ${fmt(stats.lastMonthRevenue)} last month`}
          icon={FaChartLine}
          color="text-blue-600"
          bgColor="bg-blue-100"
          change={stats.monthlyChange}
        />
        <StatCard
          title="Outstanding"
          value={fmt(stats.totalOutstanding)}
          sub={`${stats.openCount} awaiting payment`}
          icon={FaFileInvoice}
          color="text-yellow-600"
          bgColor="bg-yellow-100"
        />
        <StatCard
          title="Avg. Invoice"
          value={fmt(stats.avgInvoice)}
          sub={`${stats.paymentRate}% payment rate`}
          icon={FaCheckCircle}
          color="text-purple-600"
          bgColor="bg-purple-100"
        />
      </div>

      {/* Bar Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Monthly Revenue — Last 6 Months</h3>
          <span className="text-sm text-gray-500">Paid invoices only</span>
        </div>
        {stats.monthlyTrend.every((m) => m.revenue === 0) ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <FaChartLine className="w-10 h-10 mb-3" />
            <p className="text-sm font-medium text-gray-500">No revenue data yet</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={stats.monthlyTrend}
              margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 12, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '12px', paddingTop: '16px' }}
                formatter={(value) => (value === 'revenue' ? 'Revenue ($)' : 'Invoice Count')}
              />
              <Bar
                dataKey="revenue"
                name="revenue"
                fill="#16a34a"
                radius={[4, 4, 0, 0]}
                maxBarSize={60}
              />
              <Bar
                dataKey="invoices"
                name="invoices"
                fill="#bbf7d0"
                radius={[4, 4, 0, 0]}
                maxBarSize={60}
                yAxisId="right"
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Invoice Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Invoice History</h3>
          <div className="flex gap-2 flex-wrap">
            {FILTERS.map((f) => {
              const count =
                f === 'all'
                  ? invoices.length
                  : invoices.filter((i) => getStatus(i) === f).length;
              return (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    filter === f
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {FILTER_LABELS[f]} ({count})
                </button>
              );
            })}
          </div>
        </div>

        <div className="overflow-x-auto">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <FaFileInvoice className="w-8 h-8 mb-2" />
              <p className="text-sm">
                No {filter === 'all' ? '' : FILTER_LABELS[filter].toLowerCase() + ' '}invoices
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  {['Customer', 'Amount', 'Status', 'Sent', 'Due'].map((h) => (
                    <th
                      key={h}
                      className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((invoice) => {
                  const status = getStatus(invoice);
                  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.open;
                  return (
                    <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900">
                          {invoice.customerName || '—'}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">{invoice.email}</p>
                      </td>
                      <td className="px-6 py-4 font-semibold text-gray-900">
                        {fmt(invoice.totalAmount || 0)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${cfg.color}`}
                        >
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-500">{fmtDate(invoice.sentAt)}</td>
                      <td className="px-6 py-4 text-gray-500">{fmtDate(invoice.dueDate)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {filtered.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-500 flex justify-between">
            <span>
              Showing {filtered.length} invoice{filtered.length !== 1 ? 's' : ''}
            </span>
            {filter !== 'all' && (
              <span className="font-semibold text-gray-700">
                Total: {fmt(filtered.reduce((s, i) => s + (i.totalAmount || 0), 0))}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RevenueTab;